import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { NAV_ITEMS } from "../constants";
import { useData } from "../context/DataContext";
import { RegistrationModal } from "./RegistrationModal";
import { SupporterModal } from "./SupporterModal";
import { ScrollToTop } from "./ui";
import { Header } from "./Header";
import { Footer } from "./Footer";

export const Layout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const {
    data,
    isSupporterModalOpen,
    setIsSupporterModalOpen,
    isRegistrationModalOpen,
    setIsRegistrationModalOpen,
  } = useData();

  const {
    appName,
  } = (data && data.siteConfig) ? data.siteConfig : {} as any;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top on route change & close mobile menu & Update Title
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setIsScrolled(false);

    // Dynamic Title based on route
    const path = location.pathname.split("/").filter(Boolean);
    const pageName =
      path.length > 0
        ? path[path.length - 1]
            .replace(/-/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())
        : "Início";
    document.title = `${pageName} | ${appName || "Games no Linux"}`;

    // SEO Description injection (simple version)
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && data?.siteConfig) {
      const desc =
        data.siteConfig.serverDescription ||
        "Explore o servidor Games no Linux Hytale.";
      metaDesc.setAttribute(
        "content",
        `Explore ${pageName} no servidor Games no Linux Hytale. ${desc.slice(0, 100)}...`,
      );
    }
  }, [location.pathname, appName, data?.siteConfig?.serverDescription]);

  // Filter nav items based on admin settings
  const filteredNavItems = NAV_ITEMS.map(item => {
    if (!item.subItems) return item;
    
    const filteredSubItems = item.subItems.filter(sub => {
      if (sub.path === '/skins') return data?.libraryConfig?.sections?.skins?.enabled !== false;
      if (sub.path === '/rules') return data?.libraryConfig?.sections?.rules?.enabled !== false;
      if (sub.path === '/mods') return data?.libraryConfig?.sections?.mods?.enabled !== false;
      return true;
    });

    if (filteredSubItems.length === 0 && !item.path) return null;
    return { ...item, subItems: filteredSubItems };
  }).filter(Boolean) as typeof NAV_ITEMS;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 selection:bg-[#14B8A6] selection:text-white flex flex-col" style={{ fontFamily: 'var(--font-body)' }}>
      <Header 
        data={data}
        filteredNavItems={filteredNavItems}
        setIsSupporterModalOpen={setIsSupporterModalOpen}
        setIsRegistrationModalOpen={setIsRegistrationModalOpen}
      />

      {/* Main Content */}
      <main
        id="main-content"
        className={`flex-1 ${location.pathname === "/" ? "pt-0" : "pt-16 md:pt-24"}`}
      >
        {children}
      </main>
      <ScrollToTop />

      <Footer data={data} />

      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
      />

      <SupporterModal
        isOpen={isSupporterModalOpen}
        onClose={() => setIsSupporterModalOpen(false)}
      />
    </div>
  );
};

// Re-export UI components from ui.tsx for backward compatibility
export { SectionHeader, Card, Shimmer, Badge, Alert, Breadcrumbs, ScrollToTop, InfoTooltip, CardSkeleton, ListSkeleton } from './ui';
